import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ComputerIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V15C20 16.1046 19.1046 17 18 17H6C4.89543 17 4 16.1046 4 15V6Z"
            fill="currentColor" fillOpacity="0.15" />
        <path d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V15C20 16.1046 19.1046 17 18 17H6C4.89543 17 4 16.1046 4 15V6Z"
            className="stroke-[1.5]" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 21H16M12 17V21" className="stroke-[1.5]" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
    </svg>
);

const PhoneIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" /></svg>;
const GlobeIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>;

const DeviceRegistrationModal = ({ isOpen = true, deviceName, onAccept, onDecline }) => {
    // We'll use framer-motion for visibility instead of local state triggers

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center">
                    {/* Backdrop with enhanced blur */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-md"
                        onClick={onDecline}
                    />

                    {/* Modal Container */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="relative w-full max-w-[440px] mx-4"
                    >
                        <div className="bg-[var(--bg-secondary)] border border-white/5 rounded-[1.5rem] shadow-2xl overflow-hidden backdrop-blur-3xl">

                            {/* Decorative Background Mesh - Kept subtle and neutral */}
                            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--accent-primary)]/10 blur-[40px] rounded-full" />
                            </div>

                            {/* Header Section */}
                            <div className="relative px-8 pt-8 pb-4 text-center">
                                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/[0.05] shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] flex items-center justify-center shrink-0 transition-all backdrop-blur-sm">
                                    <ComputerIcon className="w-7 h-7 text-[var(--accent-primary)] drop-shadow-sm opacity-80" />
                                </div>
                                <h2 className="text-xl font-light text-[var(--text-main)] tracking-tight mb-0.5 transition-all">
                                    Register This Device
                                </h2>
                                <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-[0.2em] opacity-40">
                                    {deviceName || 'Unknown computer'}
                                </p>
                            </div>

                            {/* Body content */}
                            <div className="px-8 py-2 space-y-4 relative z-10">
                                <p className="text-[13px] text-[var(--text-main)] leading-relaxed text-center font-normal px-2 opacity-70">
                                    Connect this computer to your account to enable remote AI orchestration and multi-device workflows.
                                </p>

                                {/* Features List */}
                                <div className="space-y-2 pt-2">
                                    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 group hover:bg-white/[0.04] transition-all">
                                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 shadow-sm">
                                            <PhoneIcon className="w-4 h-4 text-emerald-400 opacity-70" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[12px] font-bold text-[var(--text-main)] tracking-tight">Mobile Access</p>
                                            <p className="text-[10px] text-[var(--text-muted)] font-medium leading-tight opacity-60">Control your tasks from anywhere</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 group hover:bg-white/[0.04] transition-all">
                                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 shadow-sm">
                                            <GlobeIcon className="w-4 h-4 text-blue-400 opacity-70" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[12px] font-bold text-[var(--text-main)] tracking-tight">Cloud Sync</p>
                                            <p className="text-[10px] text-[var(--text-muted)] font-medium leading-tight opacity-60">Seamless project sync across devices</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div className="px-8 pt-8 pb-8 flex flex-col gap-2 relative z-10">
                                <button
                                    onClick={onAccept}
                                    className="w-full py-3 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-slate-950 dark:text-slate-900 text-[13px] font-black rounded-xl shadow-xl transition-all active:scale-95 group overflow-hidden relative"
                                >
                                    <span className="relative z-10">Register My Device</span>
                                </button>

                                <button
                                    onClick={onDecline}
                                    className="w-full py-2 text-[11px] font-bold text-[var(--text-muted)] hover:text-red-400 transition-all uppercase tracking-[0.2em] opacity-40 hover:opacity-100"
                                >
                                    Maybe Later
                                </button>
                            </div>

                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default DeviceRegistrationModal;
